import React from "react";
import "./CRMDevelopment.css";

// TODO: update these logo paths to your own assets / CDN
// OPEN-SOURCE CRM LOGOS VIA CDN
const salesforceLogo =
  "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/salesforce.svg";

const hubspotLogo =
  "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/hubspot.svg";

const dynamicsLogo =
  "https://res.cloudinary.com/dqvvwti2y/image/upload/v1764449326/Dynamics-365-Emblem_ppysuo.png";

const zohoLogo =
  "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/zoho.svg";

const customCrmLogo =
  "https://cdn-icons-png.flaticon.com/512/992/992651.png"; // gear/settings CRM icon


const crmServices = [
  {
    title: "Custom CRM Development",
    tag: "Bespoke",
    desc: "CRM platforms tailored to your sales, service and operations workflows instead of forcing you into generic templates.",
    logo: customCrmLogo,
  },
  {
    title: "Salesforce CRM Solutions",
    tag: "Salesforce",
    desc: "Implementation, custom objects, automation and integrations for Sales Cloud, Service Cloud and Experience Cloud.",
    logo: salesforceLogo,
  },
  {
    title: "Microsoft Dynamics 365 CRM",
    tag: "Dynamics 365",
    desc: "Design and rollout of Dynamics 365 for sales, service and marketing, integrated with Microsoft 365 and Power Platform.",
    logo: dynamicsLogo,
  },
  {
    title: "HubSpot CRM Implementation",
    tag: "HubSpot",
    desc: "Setup, pipeline configuration, automation and reporting in HubSpot CRM for marketing, sales and customer success teams.",
    logo: hubspotLogo,
  },
  {
    title: "Zoho CRM Setup & Customisation",
    tag: "Zoho",
    desc: "Zoho CRM deployments with custom modules, workflows and integrations across the Zoho One ecosystem.",
    logo: zohoLogo,
  },
  {
    title: "CRM Integration & API Development",
    tag: "Integration",
    desc: "Connecting your CRM with ERPs, marketing tools, telephony, billing and support platforms using secure APIs.",
  },
  {
    title: "CRM Data Migration & Cleansing",
    tag: "Migration",
    desc: "Safe migration from legacy CRMs and spreadsheets, including data deduplication, field mapping and enrichment.",
  },
  {
    title: "Workflow Automation & Sales Playbooks",
    tag: "Automation",
    desc: "Lead routing, task automation, SLAs and playbooks so your teams follow consistent, high-conversion processes.",
  },
  {
    title: "Analytics, Dashboards & RevOps",
    tag: "Analytics",
    desc: "Deal tracking, pipeline health, revenue forecasting and cohort analysis for sales leaders and RevOps teams.",
  },
  {
    title: "CRM Support, Training & Optimization",
    tag: "Enablement",
    desc: "Ongoing admin, user training and feature rollouts to keep your CRM adopted, clean and aligned with the business.",
  },
];

const platforms = [
  { name: "Salesforce", logo: salesforceLogo },
  { name: "HubSpot", logo: hubspotLogo },
  { name: "Dynamics 365", logo: dynamicsLogo },
  { name: "Zoho CRM", logo: zohoLogo },
  { name: "Custom CRM", logo: customCrmLogo },
];

const portfolioProjects = [
  {
    title: "B2B SaaS – US & EU Sales Teams",
    type: "Salesforce RevOps",
    result: "32% increase in qualified opportunities and fully automated lead routing from marketing to AE handoff.",
    stack: "Salesforce Sales Cloud, HubSpot, Clearbit, custom API integrations",
  },
  {
    title: "Multi-location Healthcare Network",
    type: "Dynamics 365 Customer Service",
    result: "Reduced patient response times by 40% with unified tickets, call logs and follow-ups inside a single CRM.",
    stack: "Dynamics 365, Power Automate, Twilio, Azure Functions",
  },
  {
    title: "E-commerce & DTC Brand Collective",
    type: "HubSpot & Zoho Hybrid",
    result: "Single view of customer across 4 brands, with shared playbooks and reporting across all markets.",
    stack: "HubSpot CRM, Zoho CRM, Shopify, Klaviyo, custom middleware",
  },
];

const processSteps = [
  {
    title: "1. Audit & Strategy",
    desc: "We map your current tools, data flows and team workflows, then recommend the right CRM stack and architecture.",
  },
  {
    title: "2. Build, Integrate & Migrate",
    desc: "We configure the CRM, migrate data, set up integrations and define playbooks for sales, marketing and service.",
  },
  {
    title: "3. Launch, Train & Optimize",
    desc: "We run go-live, train your users, and refine dashboards, automations and reports based on real usage.",
  },
];

const industries = [
  "SaaS & Tech",
  "B2B Services",
  "E-commerce & DTC",
  "Healthcare & Clinics",
  "Financial Services",
  "Agencies & Creatives",
];

const CRMDevelopment = () => {
  return (
    <section className="crm-section">
      <div className="crm-inner">
        {/* Hero / top intro */}
        <header className="crm-header">
          <p className="crm-eyebrow">CRM DEVELOPMENT & IMPLEMENTATION</p>
          <h1 className="crm-title">
            Modern CRM Development Services for US Teams
          </h1>
          <p className="crm-sub">
            We help US businesses design, build and integrate CRM platforms that
            sales, marketing and support teams actually love using — from
            custom-built solutions to Salesforce, Dynamics 365, HubSpot, Zoho
            and more.
          </p>

          <div className="crm-pill-row">
            <span className="crm-pill">Custom CRM builds</span>
            <span className="crm-pill">Top SaaS CRMs</span>
            <span className="crm-pill">Integration &amp; RevOps</span>
          </div>
        </header>

        {/* Platform logos / “images” row */}
        <div className="crm-platforms">
          <span className="crm-platforms-label">
            Platforms we build and optimise:
          </span>
          <div className="crm-platforms-logos">
            {platforms.map((p, idx) => (
              <div key={idx} className="crm-platform-pill">
                {p.logo && (
                  <img
                    src={p.logo}
                    alt={p.name}
                    className="crm-platform-logo"
                    loading="lazy"
                  />
                )}
                <span>{p.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Services grid */}
        <section className="crm-block">
          <div className="crm-block-header">
            <h2 className="crm-section-title">
              Top CRM Development Services We Offer
            </h2>
            <p className="crm-section-sub">
              Whether you’re replacing spreadsheets or consolidating multiple
              tools, we help you pick the right CRM stack and turn it into a
              reliable revenue engine.
            </p>
          </div>

          <div className="crm-grid">
            {crmServices.map((item, idx) => (
              <article key={idx} className="crm-card">
                <div className="crm-card-top">
                  {item.logo ? (
                    <div className="crm-card-icon">
                      <img
                        src={item.logo}
                        alt={item.tag}
                        className="crm-card-icon-img"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="crm-card-icon crm-card-icon-fallback">
                      {item.tag.slice(0, 2)}
                    </div>
                  )}
                  <span className="crm-card-tag">{item.tag}</span>
                </div>
                <h3 className="crm-card-title">{item.title}</h3>
                <p className="crm-card-desc">{item.desc}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Portfolio / sample projects */}
        <section className="crm-block">
          <div className="crm-block-header">
            <h2 className="crm-section-title">Recent CRM projects</h2>
            <p className="crm-section-sub">
              A snapshot of how we’ve helped US teams unify their data, tighten
              processes and unlock better decisions with CRM.
            </p>
          </div>

          <div className="crm-portfolio-grid">
            {portfolioProjects.map((proj, idx) => (
              <article key={idx} className="crm-portfolio-card">
                <p className="crm-portfolio-type">{proj.type}</p>
                <h3 className="crm-portfolio-title">{proj.title}</h3>
                <p className="crm-portfolio-result">
                  <strong>Impact:</strong> {proj.result}
                </p>
                <p className="crm-portfolio-stack">
                  <strong>Stack:</strong> {proj.stack}
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* Process + industries */}
        <section className="crm-process-row">
          <div className="crm-process">
            <h2 className="crm-section-title">How we run CRM projects</h2>
            <p className="crm-section-sub">
              From first workshop to full rollout, we keep stakeholders aligned
              and minimise disruption to day-to-day operations.
            </p>
            <div className="crm-process-grid">
              {processSteps.map((step, idx) => (
                <div key={idx} className="crm-process-item">
                  <span className="crm-process-step">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="crm-industries">
            <h2 className="crm-section-title">Industries we serve</h2>
            <p className="crm-section-sub">
              Playbooks that respect the realities of your market instead of
              pretending every business is the same.
            </p>
            <div className="crm-industry-row">
              {industries.map((item, idx) => (
                <span key={idx} className="crm-industry-chip">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </section>
      </div>
    </section>
  );
};

export default CRMDevelopment;
